import { createFileRoute, redirect } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { trpc } from '../../trpc';
import { useUser } from '@clerk/clerk-react';
import * as Sentry from '@sentry/react';
import { AnalyticsDashboard } from '@/components/analytics/AnalyticsDashboard';
import { getEffectiveUserTier } from '../../utils/planUtils';

// Helper function to wait for Clerk to load
const waitForClerk = async (maxAttempts = 50) => {
  for (let i = 0; i < maxAttempts; i++) {
    if (window.Clerk?.user !== undefined) {
      return window.Clerk.user;
    }
    await new Promise(resolve => setTimeout(resolve, 100));
  }
  return window.Clerk?.user;
};

export const Route = createFileRoute('/dashboard/analytics')({
  component: AnalyticsContent,
  beforeLoad: async () => {
    // Wait for Clerk to load
    const user = await waitForClerk();
    if (!user) {
      return redirect({
        to: '/',
        replace: true,
      });
    }
  },
});

function AnalyticsContent() {
  const { t } = useTranslation();
  const { user } = useUser();
  const userId = user?.id;

  // Get user's plan from user table
  const { data: userPlan } = trpc.users.getUserPlan.useQuery(undefined, {
    retry: false,
    staleTime: 60000,
  });

  // Get subscription details
  const { data: subscription } = trpc.stripe.getActiveSubscription.useQuery(
    undefined,
    {
      retry: false,
      staleTime: 60000,
    }
  );

  // Get the current plan and subscription plan
  const currentPlan = userPlan?.planType || 'FREE_TRIAL';
  const subscriptionPlan = subscription?.planType;

  // For UI/display purposes, use the selected plan during trial period
  const effectivePlan = getEffectiveUserTier(
    currentPlan,
    subscriptionPlan,
    false
  );

  // For feature access, trial users always get POWER features
  const featureAccessPlan = getEffectiveUserTier(
    currentPlan,
    subscriptionPlan,
    true
  );

  // Get applications from the backend
  const applicationsQuery = trpc.applications.getAll.useQuery(
    { userId: userId || '' },
    {
      retry: 2,
      retryDelay: 500,
      onError: (error: Error) => {
        console.error('Applications query error:', error);
        Sentry.captureException(error, {
          tags: { component: 'AnalyticsContent', action: 'applicationsQuery', userId },
        });
      },
      enabled: !!userId,
      refetchOnWindowFocus: true,
      refetchOnMount: true,
      staleTime: 10000,
      refetchInterval: 30000,
    }
  );

  return (
    <>
      {applicationsQuery.isLoading ? (
        <p>{t('dashboard.loadingAnalytics')}</p>
      ) : applicationsQuery.error ? (
        <p className='text-red-500'>
          {t('common.genericError', 'Something went wrong. Please try again.')}
        </p>
      ) : (
        <AnalyticsDashboard
          applications={applicationsQuery.data || []}
          isLiteTier={featureAccessPlan === 'STARTER'}
          currentPlan={effectivePlan}
        />
      )}
    </>
  );
}
